import { ROUTES } from '@/routes/paths';
import type { NotificationPayload } from '@/types/notifications';

// Каталог типов уведомлений. Источник истины — backend `NotificationType.java` (значения lowercase,
// поле `displayable`). SSE-обёртка `displayable` не несёт, поэтому видимость/действие определяем здесь.
//
// Системные типы (`displayable=false`) → выполняем действие, в колокольчике НЕ показываем.
// Всё, чего нет среди системных, считаем видимым (история REST уже фильтрует `displayable=true`;
// неизвестный новый тип покажется в ленте без перехода — безопасный дефолт).

export type SystemActionKind = 'dealUpdate' | 'userInfoUpdate' | 'accreditationUpdate' | 'logout' | 'ignore';

const SYSTEM_ACTIONS: Readonly<Record<string, SystemActionKind>> = {
  // Двигают сделку → инвалидация её кэша (источник истины шага — всегда GET /steps).
  // ВНИМАНИЕ: на бэке реально опечатка — двойная «с» в "proccesing".
  bargain_proccesing_step_updated: 'dealUpdate',
  bargain_docs_updated: 'dealUpdate',
  bargain_cancelled_hidden: 'dealUpdate',
  // Реквизиты/данные стороны изменились → перечитать профиль/компанию/обязательные данные/сделку.
  user_info_updated: 'userInfoUpdate',
  // Аккаунт удалён → принудительный logout.
  user_deleted: 'logout',
  // Keep-alive / технические — игнорируем.
  ping: 'ignore',
  test: 'ignore',
  // Скрытые события смены статуса документов аттестации (анкета/агентский договор подписаны или
  // пересозданы) → перечитать документы «Моя компания → Аттестация», чтобы бейдж («Ожидает подписи»
  // → «Анкета принята») обновлялся без перезагрузки. Видимую пару этих событий показывает колокольчик.
  document_is_signed_hidden: 'accreditationUpdate',
  verified_profile_recreated_hidden: 'accreditationUpdate',
  profile_has_signed_internal: 'accreditationUpdate',
  // Прочие скрытые/внутренние типы вне текущего scope (ЭДО, доступ, баланс): экранов-обработчиков пока
  // нет. Перечислены явно, чтобы они НЕ попадали в колокольчик как «видимые». Обработчики добавятся
  // вместе с соответствующими разделами — это не техдолг, а зафиксированная граница scope.
  diadoc_invitation_in_hidden: 'ignore',
  diadoc_invitation_out_hidden: 'ignore',
  diadoc_credentials_are_wrong: 'ignore',
  premoderation_result_attached_to_response_internal: 'ignore',
  access_request_internal: 'ignore',
  access_result_for_owner: 'ignore',
  payment_status_update: 'ignore',
  requests_balance_update: 'ignore',
};

/**
 * Действие для системного типа, либо undefined. undefined ⇒ тип видимый (показываем в колокольчике):
 * `displayable` в SSE-обёртке не приходит, поэтому видимость = «не системный».
 */
export function systemAction(type: string): SystemActionKind | undefined {
  return SYSTEM_ACTIONS[type.toLowerCase()];
}

// Любое видимое уведомление с `bargainId` в payload относится к сделке → ведёт на её карточку
// (проверено на live SSE: так bargainId несут не только `bargain_*`, но и ошибки генерации документов
// `ref_doc_generation_*_error`/`ukd_*_error`, напоминания и т.п.). Исключение — `diadoc_*`: это внешнее
// действие в ЭДО, а не шаг сделки (старый фронт по нему тоже не навигировал). Модерация/доступ
// bargainId не несут → перехода нет, пока не появятся их разделы.
const NO_DEAL_NAV = new Set([
  'diadoc_invitation_in',
  'diadoc_invitation_out',
  'diadoc_signature_in',
  'diadoc_signature_out',
]);

// Уведомления перевозчика («новая заявка», «скоро погрузка») несут `requestId` — идентификатор заявки
// на перевозку, а не сделки. Привязываемся к ПРЕФИКСУ ТИПА, а не к одному лишь наличию поля: одноимённое
// поле уже есть у другого события — `moderation_new_package` кладёт в `requestId` пакет модерации, и
// правило «любой тип с requestId» увело бы по нему в кабинет перевозчика.
const TRUCKING_TYPE_PREFIX = 'trucking_';

/** Куда вести по клику на уведомление. null — перехода нет (просто помечаем прочитанным). */
export function navTargetFor(type: string, payload: NotificationPayload | undefined): string | null {
  const lowerType = type.toLowerCase();

  if (lowerType.startsWith(TRUCKING_TYPE_PREFIX)) {
    const requestId = payload?.requestId;
    // Заявку могли уже взять — карточка честно об этом скажет (§5.8a), поэтому ведём именно в неё:
    // там всё, что нужно для решения о взятии.
    return typeof requestId === 'number' ? `${ROUTES.APP_CARRIER_REQUESTS}/${requestId}` : null;
  }

  const id = payload?.bargainId;
  if (typeof id !== 'number' || NO_DEAL_NAV.has(lowerType)) {
    return null;
  }
  return `${ROUTES.APP_DEALS}/${id}`;
}
